<template>
  <nuxt-fetch ref="fetcher" :fetch-fn="fetchFn">
    <template #default="{data, fetching}">
      <div>
        <script type="application/ld+json" v-html="jsonLd(data)" />
        <div class="flex">
          <div style="height: 110px;" class="flex items-center relative justify-start pl-5 filters-button">
            <div
              class="absolute z-0 left-0 top-0 bottom-0 lg:bg-sky_grey_darker"
              :style="{
                width: showFilters ? '260px' : '0px',
                transitionDuration: '0.3s',
              }"
            />
            <div class="flex lg:items-center relative z-1 sky__filter-btn-wrapper">
              <div>
                <sky-btn
                  v-if="data.facets.length"
                  rounded
                  :class="showFilters || fetching ? 'bg-sky_blue text-white' : 'border-sky_grey_darker'"
                  :loading="fetching"
                  @click="showFilters = !showFilters"
                >
                  <span class="text-xxs">Filtres</span>
                  <template #append>
                    <span>
                      <icon-nav-filter class="sky__icon ml-3" :class="{dark: showFilters}" style="width: 12px;" />
                    </span>
                  </template>
                </sky-btn>
              </div>
              <div v-if="showFilters || facets.length" class="ml-3 hidden lg:block">
                <span class="block font-bold text-xs">
                  Filtres actifs: {{ facets.length }}
                </span>
                <a
                  href="#"
                  class="block text-xxs text-sky_grey"
                  @click.prevent="() => {
                    facets = []
                    $refs.fetcher.$fetch()
                  }"
                >
                  Réinitialiser
                </a>
              </div>
            </div>
          </div>
          <div class="flex-grow mx-4 flex items-center">
            <div class="lg:block flex-grow overflow-hidden flex relative" style="height: 40px;">
              <div class="absolute top-0 left-0 right-0 bottom-0">
                <facet-groups
                  :category="{
                    ...category,
                    ...data
                  }"
                  :filter="facets"
                  @add-filter="facets = $event"
                  @cancel="facets = []"
                />
              </div>
            </div>
            <div class="hidden lg:block mx-4 flex-shrink-0">
              <category-search-input :slug="category.slug" placeholder="Rechercher parmi les produits de la page" small />
            </div>
          </div>
        </div>
        <div class="flex relative z-0">
          <div v-if="showFilters" class="fixed lg:hidden left-0 right-0 bottom-0 bg-sky_blue z-10 opacity-70" style="top: 50px;" @click="showFilters = false" />
          <div
            class="fixed lg:static bg-white lg:bg-sky_grey_darker flex-shrink-0 z-10 left-0 bottom-0 overflow-y-auto overflow-x-hidden lg:overflow-hidden"
            :style="{
              width: showFilters ? '260px' : '0px',
              transitionDuration: '0.3s',
              top: '50px'
            }"
          >
            <div class="lg:hidden mb-5 uppercase tracking-wide font-bold flex justify-start items-center cursor-pointer px-5 pt-5" @click="showFilters = false">
              <icon-arrow class="mr-2" style="width: 13px; height:13px;" left />
              Fermer
            </div>
            <div style="width: 260px;" class="px-5">
              <category-filters
                v-model="facets"
                :category="{
                  ...category,
                  ...data
                }"
              />
            </div>
          </div>
          <div
            class="px-4 flex-grow flex justify-center"
          >
            <div class="flex-grow" style="max-width: 1600px;">
              <div v-if="isServerRenderedPage && page > 1" class="flex justify-center">
                <a
                  :href="previousPageUrl"
                >
                  <sky-btn
                    arrow-right
                    dark
                    class="bg-sky_blue text-white mb-10"
                  >
                    Voir les produits précédents
                  </sky-btn>
                </a>
              </div>
              <category-products-grid :category="category" :result="data" />
              <div class="flex justify-center text-center">
                <div>
                  <template v-if="!isServerRenderedPage">
                    <div class="text-xs mb-2">
                      Vous avez vu {{ data.hits.length }} produits sur {{ data.total }}
                    </div>
                    <div
                      :style="{
                        width: '232px'
                      }"
                      class="mb-4 bg-sky_grey_darker"
                    >
                      <div
                        :style="{
                          width: (data.hits.length / data.total) * 100 + '%',
                          height: '7px'
                        }"
                        class="bg-sky_secondary"
                      />
                    </div>
                  </template>
                  <a
                    v-if="mustDisplayNextPageButton(data)"
                    :href="nextPageUrl"
                    @click="(e) => {
                      if (isServerRenderedPage) {
                        return null
                      }
                      e.preventDefault()
                      fetchMore(data)
                    }"
                  >
                    <sky-btn
                      arrow-right
                      dark
                      class="bg-sky_blue text-white mb-10"
                      :loading="fetchingMore"
                    >
                      Voir plus de produits
                    </sky-btn>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #fetching>
      <div style="height: 800px;" />
    </template>
  </nuxt-fetch>
</template>

<script>
import NuxtFetch from '@hammerbot/nuxt-fetch'
import { snakeCase } from 'snake-case'
import ProductCard from '../general/ProductCard'
import IconArrow from '../general/icons/IconArrow'
import CategoryProductsGrid from './CategoryProductsGrid'
import CategoryFilters from './facets/CategoryFilters'
import FacetGroups from './facets/FacetGroups'
import CategorySearchInput from './CategorySearchInput'

export default {
  components: {
    ProductCard,
    CategoryProductsGrid,
    CategoryFilters,
    NuxtFetch,
    IconArrow,
    FacetGroups,
    CategorySearchInput
  },
  props: {
    category: {
      required: true,
      type: Object
    }
  },
  data () {
    return {
      showFilters: false,
      facets: [],
      page: this.$route.query.page && !isNaN(parseInt(this.$route.query.page)) ? parseInt(this.$route.query.page) : 1,
      fetchingMore: false,
      isServerRenderedPage: !!this.$route.query.page
    }
  },
  computed: {
    nextPageUrl () {
      return `${this.$route.path}?page=${this.page + 1}`
    },
    previousPageUrl () {
      if (this.page - 1 === 0) {
        return null
      } else if (this.page - 1 === 1) {
        return this.$route.path
      }
      return `${this.$route.path}?page=${this.page - 1}`
    }
  },
  watch: {
    '$store.state.auth.preferences' () {
      this.$refs.fetcher.$fetch()
    },
    async facets (value) {
      // Must construct URI
      let facets = ''
      for (const item of value) {
        facets += `/${snakeCase(item.feature_id)}`
        for (const facetValue of item.values) {
          facets += `-${snakeCase(facetValue)}`
        }
      }
      const newUri = `${this.$helpers.makeUri(this.category.hierarchy[this.category.hierarchy.length - 1].path, 'category')}${facets.length ? `caracteristiques${facets}/` : ''}`
      history.replaceState(null, null, newUri)
      await this.$refs.fetcher.$fetch()
    }
  },
  mounted () {
    setTimeout(() => {
      if ((!!this.category.properties.default_open_filters || this.$route.path.match(/^(.*)\/caracteristiques\/(.*)$/)) && this.$store.state.screen.width >= 1024) {
        this.showFilters = true
      }
    }, 200)
    this.$nextTick(async () => {
      // Initiate facets with URL
      const filterMatch = this.$route.path.match(/^(.*)\/caracteristiques\/(.*)$/)
      if (filterMatch) {
        const { facets } = await this.fetchFn()
        const explodedFilterMatch = filterMatch[2].split('/').filter(e => e)
        const facetsToAssign = []
        for (const exploded of explodedFilterMatch) {
          const explodedFeaturePart = exploded.split('-')
          const correspondingFacet = facets.find(f => snakeCase(f.feature_id) === explodedFeaturePart[0])
          if (!correspondingFacet) {
            continue
          }
          const facetValues = []
          for (let i = 1; i < explodedFeaturePart.length; i++) {
            const foundValue = correspondingFacet.values.find(v => snakeCase(v.text) === explodedFeaturePart[i])
            if (!foundValue) {
              continue
            }
            facetValues.push(foundValue.text)
          }
          if (!facetValues.length) {
            continue
          }
          facetsToAssign.push({
            feature_id: correspondingFacet.feature_id,
            type: 'term',
            values: facetValues
          })
        }
        this.facets = facetsToAssign
      }
    })
  },
  methods: {
    jsonLd (products) {
      const ratingCount = products.hits.reduce((prev, current) => {
        return prev + (current.properties?.reviews?.total || 0)
      }, 0)

      const ratingValue = products.hits.filter(h => h.properties?.reviews?.total).reduce((prev, current) => {
        return prev + (current?.properties?.reviews?.avg * current.properties?.reviews?.total)
      }, 0) / ratingCount

      const prices = products.hits.filter(p => p.pricing.unit_taxed_price).map(p => p.pricing.unit_taxed_price)

      const lowestPrice = Math.min(...prices)
      const highestPrice = Math.max(...prices)

      if (!products.hits.length) {
        return null
      }

      const images = []

      for (const product of products.hits.filter(p => p.cover).slice(0, 5)) {
        images.push(this.$helpers.makeCdnUrl(product.cover, product.slug, 'c', 'b'))
      }

      return {
        '@context': 'http://schema.org',
        '@type': 'Product',
        category: this.category.properties.meta_title || this.category.label,
        image: images,
        name: this.category.properties.meta_title || this.category.label,
        url: 'https://www.skylantern.fr' + this.$helpers.makeUri(this.category.path, 'category'),
        aggregateRating: ratingCount
          ? {
              '@type': 'AggregateRating',
              ratingValue: ratingValue.toFixed(2),
              ratingCount
            }
          : undefined,
        offers: {
          '@type': 'AggregateOffer',
          highPrice: highestPrice.toFixed(2),
          lowPrice: lowestPrice.toFixed(2),
          priceCurrency: 'EUR',
          offerCount: 15
        },
        brand: 'Skylantern Original',
        description: this.category.properties.meta_description
      }
    },
    async fetchFn () {
      const { data } = await this.$api.post(`/categories/${this.category.id}/products`, {
        page: this.page,
        size: 80,
        facets: this.facets
      })
      return data
    },
    async fetchMore (currentData) {
      this.fetchingMore = true
      try {
        this.page++
        const result = await this.fetchFn()
        currentData.hits.push(...result.hits)
      } catch (error) {
        this.$gtmEvents.onRuntimeError(error, 'category/fetch-more')
      }
      this.fetchingMore = false
    },
    mustDisplayNextPageButton (data) {
      if (this.isServerRenderedPage) {
        const cursor = this.page * 80
        if (cursor >= data.total) {
          return false
        }
      }
      return data.hits.length < data.total
    }
  }
}
</script>

<style scoped>
@screen lg {
  .filters-button {
    width: 260px;
  }
}
</style>
